import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AccommodationBooking,
  AccommodationGuest,
  AccommodationProperty,
} from '../../types/accommodation';
import {
  createPropertyThunk,
  fetchAllCompanyPropertiesThunk,
  fetchPropertyThunk,
  updatePropertyThunk,
} from './accommodationThunks';

interface AccommodationState {
  property: AccommodationProperty | null;
  properties: AccommodationProperty[];
  selectedBooking: AccommodationBooking | null;
  selectedGuest: AccommodationGuest | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: AccommodationState = {
  property: null,
  properties: [],
  selectedBooking: null,
  selectedGuest: null,
  isLoading: false,
  error: null,
};

const accommodationSlice = createSlice({
  name: 'accommodation',
  initialState,
  reducers: {
    setSelectedBooking: (
      state,
      action: PayloadAction<AccommodationBooking | null>,
    ) => {
      state.selectedBooking = action.payload;
    },
    setSelectedGuest: (
      state,
      action: PayloadAction<AccommodationGuest | null>,
    ) => {
      state.selectedGuest = action.payload;
    },
    clearAccommodationError: (state) => {
      state.error = null;
    },
    resetAccommodation: () => initialState,
  },
  extraReducers: (builder) => {
    // ---- Property ----
    builder
      .addCase(fetchPropertyThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPropertyThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.property = action.payload;
      })
      .addCase(fetchPropertyThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createPropertyThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(createPropertyThunk.fulfilled, (state, action) => {
        state.property = action.payload;
      })
      .addCase(createPropertyThunk.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    builder
      .addCase(updatePropertyThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(updatePropertyThunk.fulfilled, (state, action) => {
        state.property = action.payload;
      })
      .addCase(updatePropertyThunk.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // ---- All company properties ----
    builder
      .addCase(fetchAllCompanyPropertiesThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllCompanyPropertiesThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.properties = action.payload;
        if (!state.property && action.payload.length > 0) {
          [state.property] = action.payload;
        }
      })
      .addCase(fetchAllCompanyPropertiesThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedBooking,
  setSelectedGuest,
  clearAccommodationError,
  resetAccommodation,
} = accommodationSlice.actions;

export default accommodationSlice.reducer;
